
import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from 'react';
import { useScoutingState } from './ScoutingStateContext';

interface PwaUpdateContextType {
  updateAvailable: boolean;
  updateApp: () => void;
  setRegistration: (registration: ServiceWorkerRegistration) => void;
}

const PwaUpdateContext = createContext<PwaUpdateContextType | undefined>(undefined);

export const PwaUpdateProvider: React.FC<{ children: ReactNode; initialRegistration?: ServiceWorkerRegistration }> = ({
  children,
  initialRegistration
}) => {
  const [registration, setRegistrationState] = useState<ServiceWorkerRegistration | undefined>(initialRegistration);
  const [updateAvailable, setUpdateAvailable] = useState(!!initialRegistration);
  const { isScoutingActive } = useScoutingState();
  const pendingReload = useRef(false);

  const setRegistration = useCallback((reg: ServiceWorkerRegistration) => {
    setRegistrationState(reg);
    setUpdateAvailable(true);
  }, []);

  const updateApp = useCallback(() => {
    if (registration && registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  }, [registration]);

  // Listen for custom update event
  useEffect(() => {
    const handleUpdateEvent = (event: any) => {
      if (event.detail) {
        setRegistration(event.detail);
      }
    };
    window.addEventListener('pwa-update-available', handleUpdateEvent);
    return () => {
      window.removeEventListener('pwa-update-available', handleUpdateEvent);
    };
  }, [setRegistration]);

  // Listen for controllerchange — defer reload if scouting is active
  useEffect(() => {
    const sw = 'serviceWorker' in navigator ? navigator.serviceWorker : null;
    if (!sw) return;

    const handleControllerChange = () => {
      if (isScoutingActive) {
        pendingReload.current = true;
        console.warn('[PWA] Update ready but scouting is active — reload deferred.');
      } else {
        window.location.reload();
      }
    };
    sw.addEventListener('controllerchange', handleControllerChange);
    return () => {
      sw.removeEventListener('controllerchange', handleControllerChange);
    };
  }, [isScoutingActive]);

  // When scouting becomes inactive, execute any deferred reload
  useEffect(() => {
    if (!isScoutingActive && pendingReload.current) {
      pendingReload.current = false;
      window.location.reload();
    }
  }, [isScoutingActive]);

  return (
    <PwaUpdateContext.Provider value={{ updateAvailable, updateApp, setRegistration }}>
      {children}
    </PwaUpdateContext.Provider>
  );
};

export const usePwaUpdate = () => {
  const context = useContext(PwaUpdateContext);
  if (context === undefined) {
    throw new Error('usePwaUpdate must be used within a PwaUpdateProvider');
  }
  return context;
};
